/* Version: 3.5.149 */
'use client';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { BREAKPOINTS } from '../../spacing/index.js';
import { rem } from '../../utils/functions.js';
import { FocusOutlineInset } from '../../mixins/index.js';
import { CONTENT_TYPE } from './constants.js';
import { isOverlay, isAccordion } from './utils.js';
import { typography } from '../../typography/typography.js';

var MAIN_NAV_WIDTH = '80px';
var MAIN_NAV_WIDTH_WIDE = '100px';
var SUB_NAV_WIDTH = '310px';
var SUB_NAV_WIDTH_XL = '350px';
var List = /*#__PURE__*/styled.ul.withConfig({
  displayName: "styledComponents__List",
  componentId: "sc-181wobl-0"
})(["margin:0;padding:0;list-style:none;"]);
var LogoComponent = /*#__PURE__*/styled.div.withConfig({
  displayName: "styledComponents__LogoComponent",
  componentId: "sc-181wobl-1"
})(["text-decoration:none;height:", ";padding:0;margin:0;border:none;&:is(a),&:is(button){cursor:pointer;}@media (max-width:", "px){height:60px;}"], function (_ref) {
  var theme = _ref.theme,
    isWideMainNav = _ref.isWideMainNav;
  return isWideMainNav ? "".concat(theme.sideNav.brandLogo.height) : '80px';
}, BREAKPOINTS.MAX_1024);
var ItemComponent = /*#__PURE__*/styled.button.attrs(function (_ref2) {
  var as = _ref2.as;
  return as === 'button' ? {
    type: 'button'
  } : {};
}).withConfig({
  displayName: "styledComponents__ItemComponent",
  componentId: "sc-181wobl-2"
})(["font-family:", ";font-size:", ";cursor:pointer;text-decoration:none;border:none;box-sizing:border-box;margin:0;width:100%;display:flex;align-items:center;:focus-visible{", ";}"], typography.fontFamily, typography.fontSize, FocusOutlineInset);
var Root = /*#__PURE__*/styled.nav.withConfig({
  displayName: "styledComponents__Root",
  componentId: "sc-181wobl-3"
})(["user-select:none;z-index:4;position:", ";height:100%;top:", ";@media (max-width:", "px){position:fixed;display:flex;flex-wrap:wrap;align-items:center;height:60px;width:100%;top:0;background-color:", ";}"], function (_ref3) {
  var isSticky = _ref3.isSticky;
  return isSticky ? 'fixed' : 'absolute';
}, function (_ref4) {
  var isSticky = _ref4.isSticky,
    theme = _ref4.theme;
  return isSticky ? 0 : theme.sideNav.mainNav.top;
}, BREAKPOINTS.MAX_1024, function (_ref5) {
  var theme = _ref5.theme;
  return theme.sideNav.mainNav.backgroundColor;
});
process.env.NODE_ENV !== "production" ? Root.displayName = 'Root' : undefined;
var BrandLogoComponent = /*#__PURE__*/styled(LogoComponent).withConfig({
  displayName: "styledComponents__BrandLogoComponent",
  componentId: "sc-181wobl-4"
})(["display:block;background-color:", ";pointer-events:", ";position:relative;&:focus-visible{", ";&::before{content:'';position:absolute;height:100%;width:100%;top:0;left:0;", ";}}@media (max-width:", "px){width:60px;}"], function (_ref6) {
  var theme = _ref6.theme;
  return theme.colorPalette.primaryColors.main.shade_default;
}, function (_ref7) {
  var href = _ref7.href,
    onClick = _ref7.onClick;
  return !href && !onClick ? 'none' : 'auto';
}, FocusOutlineInset, FocusOutlineInset, BREAKPOINTS.MAX_1024);
process.env.NODE_ENV !== "production" ? BrandLogoComponent.displayName = 'BrandLogoComponent' : undefined;
var WordmarkLogoComponent = /*#__PURE__*/styled(LogoComponent).withConfig({
  displayName: "styledComponents__WordmarkLogoComponent",
  componentId: "sc-181wobl-5"
})(["display:flex;align-items:center;border-bottom:", ";padding:", ";&:focus-visible{", ";}@media (min-width:", "px){width:100%;}@media (max-width:", "px){border:none;padding:0;}"], function (_ref8) {
  var theme = _ref8.theme;
  return "1px solid ".concat(theme.colorPalette.neutralColors.light.shade_default);
}, function (_ref9) {
  var theme = _ref9.theme;
  return "0 ".concat(theme.sideNav.subNav.item.padding);
}, FocusOutlineInset, BREAKPOINTS.DESKTOP_SM, BREAKPOINTS.MAX_1024);
process.env.NODE_ENV !== "production" ? WordmarkLogoComponent.displayName = 'WordmarkLogoComponent' : undefined;

// Styled components for SideNav
var MobileNavButton = /*#__PURE__*/styled.button.withConfig({
  displayName: "styledComponents__MobileNavButton",
  componentId: "sc-181wobl-6"
})(["font-family:", ";font-size:", ";border:none;height:100%;width:60px;color:", ";background-color:", ";border-left:", ";cursor:pointer;margin-left:auto;&:focus-visible{", ";}& svg{display:inline;}"], typography.fontFamily, rem(10), function (_ref10) {
  var theme = _ref10.theme;
  return theme.colorPalette.neutralColors.light.shade_white;
}, function (_ref11) {
  var isSelected = _ref11.isSelected,
    theme = _ref11.theme;
  return isSelected && isOverlay(theme) ? theme.colorPalette.neutralColors.dark.shade_default : 'transparent';
}, function (_ref12) {
  var theme = _ref12.theme;
  return "1px solid ".concat(theme.colorPalette.neutralColors.dark.shade_default);
}, FocusOutlineInset);
process.env.NODE_ENV !== "production" ? MobileNavButton.displayName = 'MobileNavButton' : undefined;

// Styled components for MainNav
var MobileHeaderItem = /*#__PURE__*/styled.div.withConfig({
  displayName: "styledComponents__MobileHeaderItem",
  componentId: "sc-181wobl-7"
})(["border-bottom:", ";"], function (_ref13) {
  var theme = _ref13.theme;
  return "1px solid ".concat(theme.sideNav.mobile.header.borderColor);
});
process.env.NODE_ENV !== "production" ? MobileHeaderItem.displayName = 'MobileHeaderItem' : undefined;
var MobileFooterWrapper = /*#__PURE__*/styled.ul.withConfig({
  displayName: "styledComponents__MobileFooterWrapper",
  componentId: "sc-181wobl-8"
})(["margin:0;padding:0;"]);
process.env.NODE_ENV !== "production" ? MobileFooterWrapper.displayName = 'MobileFooterWrapper' : undefined;
var MobileFooterItem = /*#__PURE__*/styled.li.withConfig({
  displayName: "styledComponents__MobileFooterItem",
  componentId: "sc-181wobl-9"
})(["border-bottom:", ";list-style:none;"], function (_ref14) {
  var theme = _ref14.theme;
  return "1px solid ".concat(theme.sideNav.mobile.footer.borderColor);
});
process.env.NODE_ENV !== "production" ? MobileFooterItem.displayName = 'MobileFooterItem' : undefined;
var MainNavWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "styledComponents__MainNavWrapper",
  componentId: "sc-181wobl-10"
})(["height:100%;width:", ";background-color:", ";"], function (_ref15) {
  var isWideMainNav = _ref15.isWideMainNav;
  return isWideMainNav ? MAIN_NAV_WIDTH_WIDE : MAIN_NAV_WIDTH;
}, function (_ref16) {
  var theme = _ref16.theme;
  return "".concat(theme.sideNav.mainNav.backgroundColor);
});
process.env.NODE_ENV !== "production" ? MainNavWrapper.displayName = 'MainNavWrapper' : undefined;
var MobileMainNavWrapper = /*#__PURE__*/styled(motion.div).withConfig({
  displayName: "styledComponents__MobileMainNavWrapper",
  componentId: "sc-181wobl-11"
})(["--mobile-header-height:60px;height:calc(100vh - var(--mobile-header-height));min-height:calc(100vh - var(--mobile-header-height));width:100%;overflow-x:clip;overflow-y:auto;color:", ";background-color:", ";border-top:", ";"], function (_ref17) {
  var theme = _ref17.theme;
  return "".concat(theme.sideNav.mobile.color);
}, function (_ref18) {
  var theme = _ref18.theme;
  return "".concat(theme.sideNav.mobile.backgroundColor);
}, function (_ref19) {
  var theme = _ref19.theme;
  return isAccordion(theme) ? "1px solid ".concat(theme.colorPalette.neutralColors.dark.shade_default) : 'none';
});
process.env.NODE_ENV !== "production" ? MobileMainNavWrapper.displayName = 'MobileMainNavWrapper' : undefined;
var MainNavList = /*#__PURE__*/styled(List).withConfig({
  displayName: "styledComponents__MainNavList",
  componentId: "sc-181wobl-12"
})([""]);
process.env.NODE_ENV !== "production" ? MainNavList.displayName = 'MainNavList' : undefined;

// Styled components for MainNavItem
var MainItem = /*#__PURE__*/styled.li.withConfig({
  displayName: "styledComponents__MainItem",
  componentId: "sc-181wobl-13"
})(["border-bottom:", ";@media (max-width:", "px){border-bottom:", ";}"], function (_ref20) {
  var theme = _ref20.theme;
  return "1px solid ".concat(theme.colorPalette.neutralColors.dark.shade_default);
}, BREAKPOINTS.MAX_1024, function (_ref21) {
  var theme = _ref21.theme;
  return isAccordion(theme) ? "1px solid ".concat(theme.colorPalette.neutralColors.dark.shade_default) : "1px solid ".concat(theme.colorPalette.neutralColors.light.shade_default);
});
process.env.NODE_ENV !== "production" ? MainItem.displayName = 'MainItem' : undefined;
var MainItemComponent = /*#__PURE__*/styled(ItemComponent).withConfig({
  displayName: "styledComponents__MainItemComponent",
  componentId: "sc-181wobl-14"
})(["font-size:", ";color:", ";text-align:center;min-height:80px;padding:17px 10px;flex-direction:column;justify-content:", ";background-color:", ";:hover,:focus{background-color:", ";}:focus-visible{", ";}@media (max-width:", "px){font-size:", ";flex-direction:row;justify-content:left;text-align:left;padding:0 16px 0 ", ";min-height:auto;height:", ";color:", ";:hover,:focus{background-color:", ";}", "}"], function (_ref22) {
  var theme = _ref22.theme;
  return "".concat(theme.sideNav.mainNav.fontSize);
}, function (_ref23) {
  var theme = _ref23.theme;
  return "".concat(theme.colorPalette.neutralColors.light.shade_white);
}, function (_ref24) {
  var $hasIcon = _ref24.$hasIcon;
  return $hasIcon ? 'space-between' : 'center';
}, function (_ref25) {
  var theme = _ref25.theme,
    $isActive = _ref25.$isActive;
  return $isActive ? "".concat(theme.colorPalette.neutralColors.dark.shade_default) : 'transparent';
}, function (_ref26) {
  var theme = _ref26.theme;
  return theme.colorPalette.neutralColors.dark.shade_default;
}, FocusOutlineInset, BREAKPOINTS.MAX_1024, typography.fontSize, function (_ref27) {
  var theme = _ref27.theme;
  return isOverlay(theme) ? '4px' : '0';
}, function (_ref28) {
  var theme = _ref28.theme;
  return theme.sideNav.mobile.item.height;
}, function (_ref29) {
  var theme = _ref29.theme;
  return theme.sideNav.mobile.color;
}, function (_ref30) {
  var theme = _ref30.theme;
  return theme.sideNav.mobile.backgroundColor;
}, function (_ref31) {
  var theme = _ref31.theme,
    $isActive = _ref31.$isActive;
  if ($isActive && isOverlay(theme)) {
    return "\n          background-color: ".concat(theme.colorPalette.neutralColors.light.shade_light_2, ";\n          box-shadow: inset 4px 0px 0px 0px ").concat(theme.sideNav.subNav.item.selected.borderColor, ";\n        ");
  }
  return "background-color: ".concat(theme.sideNav.mobile.item.backgroundColor, ";");
});
process.env.NODE_ENV !== "production" ? MainItemComponent.displayName = 'MainItemComponent' : undefined;
var MainNavItemIconWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "styledComponents__MainNavItemIconWrapper",
  componentId: "sc-181wobl-15"
})(["height:20px;margin-bottom:10px;@media (max-width:", "px){min-width:56px;margin:0;text-align:center;}& svg{display:inline;}"], BREAKPOINTS.MAX_1024);
process.env.NODE_ENV !== "production" ? MainNavItemIconWrapper.displayName = 'MainNavItemIconWrapper' : undefined;
var MainNavItemLabel = /*#__PURE__*/styled.span.withConfig({
  displayName: "styledComponents__MainNavItemLabel",
  componentId: "sc-181wobl-16"
})(["overflow:hidden;line-height:", ";max-width:", ";@media (max-width:", "px){flex:1 1 auto;max-width:100%;}"], function (_ref32) {
  var theme = _ref32.theme;
  return "".concat(theme.sideNav.mainNav.lineHeight);
}, function (_ref33) {
  var isWideMainNav = _ref33.isWideMainNav;
  return isWideMainNav ? MAIN_NAV_WIDTH_WIDE : MAIN_NAV_WIDTH;
}, BREAKPOINTS.MAX_1024);
process.env.NODE_ENV !== "production" ? MainNavItemLabel.displayName = 'MainNavItemLabel' : undefined;

// Styled components for SubNav
var SubNavWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "styledComponents__SubNavWrapper",
  componentId: "sc-181wobl-17"
})(["height:100%;width:", ";position:absolute;overflow-y:auto;top:0;left:", ";border-right:", ";background-color:", ";overflow-x:hidden;@media (min-width:", "px){width:", ";}"], function (_ref34) {
  var theme = _ref34.theme;
  return theme.sideNav.subNav.width || SUB_NAV_WIDTH;
}, function (_ref35) {
  var isWideMainNav = _ref35.isWideMainNav;
  return isWideMainNav ? MAIN_NAV_WIDTH_WIDE : MAIN_NAV_WIDTH;
}, function (_ref36) {
  var theme = _ref36.theme;
  return "1px solid ".concat(theme.colorPalette.neutralColors.light.shade_default);
}, function (_ref37) {
  var theme = _ref37.theme;
  return theme.colorPalette.neutralColors.light.shade_white;
}, BREAKPOINTS.DESKTOP_XL, function (_ref38) {
  var theme = _ref38.theme;
  return theme.sideNav.subNav.width || SUB_NAV_WIDTH_XL;
});
process.env.NODE_ENV !== "production" ? SubNavWrapper.displayName = 'SubNavWrapper' : undefined;
var MobileSubNavWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "styledComponents__MobileSubNavWrapper",
  componentId: "sc-181wobl-18"
})(["width:100%;background-color:", ";", ""], function (_ref39) {
  var theme = _ref39.theme;
  return "".concat(theme.colorPalette.neutralColors.light.shade_white);
}, function (_ref40) {
  var theme = _ref40.theme,
    isSubNavExpanded = _ref40.isSubNavExpanded,
    isMobileNavExpanded = _ref40.isMobileNavExpanded;
  if (isOverlay(theme)) {
    var overlayStyle = isSubNavExpanded ? "height: calc(100vh - var(--mobile-header-height));\n          transition: transform 800ms;" : "overflow: hidden;\n          visibility: hidden;\n          height: 0;\n          transition: all 800ms, height 0ms 800ms;";
    return "\n        ".concat(overlayStyle, "\n        position: absolute;\n        top: 0;\n        transform: ").concat(isSubNavExpanded || !isMobileNavExpanded ? 'translateX(0)' : 'translateX(100vw)', ";\n      ");
  }
  var accordionStyle = isSubNavExpanded ? "opacity: 1;\n        max-height: 1500px;\n        visibility: visible;\n        transition: max-height 800ms ease 50ms, opacity 100ms;" : "opacity: 0;\n        max-height: 0;\n        visibility: hidden;\n        transition: all 200ms ease 50ms;";
  return "\n      ".concat(accordionStyle, "\n      overflow: hidden;\n    ");
});
process.env.NODE_ENV !== "production" ? MobileSubNavWrapper.displayName = 'MobileSubNavWrapper' : undefined;
var SubNavList = /*#__PURE__*/styled(List).withConfig({
  displayName: "styledComponents__SubNavList",
  componentId: "sc-181wobl-19"
})([""]);
process.env.NODE_ENV !== "production" ? SubNavList.displayName = 'SubNavList' : undefined;
var SubItemLabelWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "styledComponents__SubItemLabelWrapper",
  componentId: "sc-181wobl-20"
})(["display:inline-block;"]);
process.env.NODE_ENV !== "production" ? SubItemLabelWrapper.displayName = 'SubItemLabelWrapper' : undefined;

// Styled components for SubNavItem
var SubItemComponent = /*#__PURE__*/styled(ItemComponent).withConfig({
  displayName: "styledComponents__SubItemComponent",
  componentId: "sc-181wobl-21"
})(["font-weight:", ";border-bottom:", ";line-height:", ";color:", ";text-align:left;min-height:", ";padding:", ";:hover{background-color:", ";}@media (max-width:", "px){padding-left:", ";padding-right:", ";}.mux-Sidenav-dropdown-icon{margin-left:auto;}", ""], function (_ref41) {
  var isDropdownExpanded = _ref41.isDropdownExpanded;
  return isDropdownExpanded ? typography.fontWeightDemibold : typography.fontWeightNormal;
}, function (_ref42) {
  var theme = _ref42.theme;
  return "1px solid ".concat(theme.colorPalette.neutralColors.light.shade_default);
}, typography.lineHeight, function (_ref43) {
  var theme = _ref43.theme;
  return "".concat(theme.colorPalette.neutralColors.dark.shade_default);
}, function (_ref44) {
  var theme = _ref44.theme;
  return theme.sideNav.subNav.item.height;
}, function (_ref45) {
  var theme = _ref45.theme;
  return "0 ".concat(theme.sideNav.subNav.item.padding);
}, function (_ref46) {
  var theme = _ref46.theme;
  return theme.sideNav.subNav.item.selected.backgroundColor;
}, BREAKPOINTS.MAX_1024, function (_ref47) {
  var theme = _ref47.theme;
  return isAccordion(theme) ? '20px' : '16px';
}, function (_ref48) {
  var theme = _ref48.theme;
  return isAccordion(theme) ? '20px' : '16px';
}, function (_ref49) {
  var theme = _ref49.theme,
    $isActive = _ref49.$isActive,
    $contentType = _ref49.$contentType;
  if ($isActive && $contentType !== CONTENT_TYPE.BANNER) {
    return "\n        background-color: ".concat(theme.sideNav.subNav.item.selected.backgroundColor, ";\n        font-weight: ").concat(theme.sideNav.subNav.item.selected.fontWeight, ";\n        box-shadow: inset 4px 0px 0px 0px ").concat(theme.sideNav.subNav.item.selected.borderColor, ";\n      ");
  }
  return "background-color: ".concat(theme.colorPalette.neutralColors.light.shade_white, ";");
});
process.env.NODE_ENV !== "production" ? SubItemComponent.displayName = 'SubItemComponent' : undefined;
var SubNavDropdownList = /*#__PURE__*/styled(List).withConfig({
  displayName: "styledComponents__SubNavDropdownList",
  componentId: "sc-181wobl-22"
})(["", "{padding-left:", ";@media (max-width:", "px){padding-left:48px;}}"], SubItemComponent, function (_ref50) {
  var theme = _ref50.theme;
  return "min(calc(".concat(theme.sideNav.subNav.item.padding, " + 28px), 60px)");
}, BREAKPOINTS.MAX_1024);
process.env.NODE_ENV !== "production" ? SubNavDropdownList.displayName = 'SubNavDropdownList' : undefined;
var StaticHeading = /*#__PURE__*/styled.span.withConfig({
  displayName: "styledComponents__StaticHeading",
  componentId: "sc-181wobl-23"
})(["border:none;box-sizing:border-box;border-bottom:", ";color:", ";display:flex;align-items:center;font-family:", ";font-size:", ";font-weight:", ";line-height:normal;min-height:", ";margin:0;padding:", ";text-align:left;width:100%;@media (max-width:", "px){color:", ";padding:32px 16px 16px;}"], function (_ref51) {
  var theme = _ref51.theme;
  return "1px solid ".concat(theme.colorPalette.neutralColors.light.shade_default);
}, function (_ref52) {
  var theme = _ref52.theme;
  return "".concat(theme.colorPalette.neutralColors.dark.shade_default);
}, typography.fontFamily, rem(20), typography.fontWeightDemibold, function (_ref53) {
  var theme = _ref53.theme;
  return theme.sideNav.subNav.item.height;
}, function (_ref54) {
  var theme = _ref54.theme;
  return "36px ".concat(theme.sideNav.subNav.item.padding, " 18px");
}, BREAKPOINTS.MAX_1024, function (_ref55) {
  var theme = _ref55.theme;
  return "".concat(theme.colorPalette.neutralColors.dark.shade_default);
});
process.env.NODE_ENV !== "production" ? StaticHeading.displayName = 'StaticHeading' : undefined;

export { BrandLogoComponent, MAIN_NAV_WIDTH, MAIN_NAV_WIDTH_WIDE, MainItem, MainItemComponent, MainNavItemIconWrapper, MainNavItemLabel, MainNavList, MainNavWrapper, MobileFooterItem, MobileFooterWrapper, MobileHeaderItem, MobileMainNavWrapper, MobileNavButton, MobileSubNavWrapper, Root, SUB_NAV_WIDTH, SUB_NAV_WIDTH_XL, StaticHeading, SubItemComponent, SubItemLabelWrapper, SubNavDropdownList, SubNavList, SubNavWrapper, WordmarkLogoComponent };
